import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';

import { WINDOW } from './window-injection';

interface ChromeNotificationOptions extends NotificationOptions {
  requireInteraction: boolean;
}

@Injectable({ providedIn: 'root' })
export class BrowserWindowService {
  private static isFirstOnline = true;
  private httpClient = inject(HttpClient);
  private window = inject(WINDOW);

  private readonly _onWakeUp$ = new Subject<void>();
  public readonly onWakeUp$ = this._onWakeUp$.asObservable();

  public readonly isOnline = computed(() => this._isOnline());
  private _isOnline = signal(false);

  public windowIsActive = signal(false);
  public isRefreshingPage = false;

  public readonly browserOS = signal<string>(this.getOS());

  public readonly isMac = computed(() => this.browserOS() === 'mac');
  public readonly isWindows = computed(() => this.browserOS() === 'windows');
  public readonly isLinux = computed(() => this.browserOS() === 'linux');
  private isNetworkWakingUp = false;
  private apiBaseUrl: string | undefined;
  private onlinePollInterval: number | undefined;
  private notificationOverride:
    | ((id: string, title: string, message: string, onClick?: () => void, iconUrl?: string) => void)
    | undefined;

  public constructor() {
    this.windowIsActive.set(this.window.document.hasFocus());
    this.window.addEventListener('focus', () => {
      this.windowIsActive.set(true);
      this._onWakeUp$.next();
    });
    this.window.addEventListener('blur', () => {
      this.windowIsActive.set(false);
    });

    this.window.document.addEventListener('visibilitychange', () => {
      if (this.window.document.visibilityState === 'visible') {
        this._onWakeUp$.next();
      }
    });

    this._isOnline.set(this.window.navigator.onLine);

    this.window.addEventListener('online', () => {
      this.stopOnlinePolling();
      if (!BrowserWindowService.isFirstOnline) {
        const isOnline = this._isOnline();
        if (isOnline) {
          // if we are firing online and it's NOT the first time,
          // assume the offline event wasn't fired. Manually mark as offline
          // so the when we set ourselves to online, we go through the data refresh
          this._isOnline.set(false);
        }
      }
      this.isNetworkWakingUp = true;
      setTimeout(() => {
        if (!this.isNetworkWakingUp) {
          // if we went back offline while waking up,
          // don't set online to true
          return;
        }
        this._isOnline.set(true);
        BrowserWindowService.isFirstOnline = false;
      }, 2000);
    });

    this.window.addEventListener('offline', () => {
      this._isOnline.set(false);
    });

    effect(() => {
      const isOnline = this._isOnline();
      if (isOnline) {
        this.stopOnlinePolling();
      }
    });
  }

  public registerApiUrl(url: string) {
    this.apiBaseUrl = url;
  }

  public registerNotificationOverride(
    override: (
      id: string,
      title: string,
      message: string,
      onClick?: () => void,
      iconUrl?: string,
    ) => void,
  ) {
    this.notificationOverride = override;
  }

  public refreshPage() {
    if (this.isRefreshingPage) {
      return;
    }

    this.isRefreshingPage = true;

    this._isOnline.set(false);
    setTimeout(() => {
      this._isOnline.set(true);
      this.isRefreshingPage = false;
    }, 200);
  }

  public showNotification(
    id: string,
    title: string,
    message: string,
    onClick?: () => void,
    iconUrl?: string,
  ) {
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notification');
    } else if (Notification['permission'] === 'granted') {
      this.sendNotification(id, title, message, onClick, iconUrl);
    } else {
      console.warn('Notification Permission: ', Notification['permission']);
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.sendNotification(id, title, message, onClick, iconUrl);
        }
      });
    }
  }

  public startOnlinePolling(onlineHandler: () => void) {
    this.stopOnlinePolling();

    this.onlinePollInterval = setInterval(() => {
      this.pollForInternet(onlineHandler);
    }, 5000);
  }

  public stopOnlinePolling(markOnline = false) {
    if (this.onlinePollInterval) {
      clearInterval(this.onlinePollInterval);
      this.onlinePollInterval = undefined;
    }

    if (this.window.navigator.onLine && markOnline) {
      this._isOnline.set(true);
    }
  }

  public async pingApi() {
    const api = `api/deployment`;
    const obs$ = this.httpClient.get<{ version: string }>(api);
    const result = await firstValueFrom(obs$).catch(() => {
      return undefined;
    });

    return !!result;
  }

  private async pollForInternet(onlineHandler: () => void) {
    if (!this.apiBaseUrl) {
      return;
    }

    const isOnline = await this.pingApi();
    const isBrowserOnline = this._isOnline();

    if (isOnline && !isBrowserOnline) {
      this._isOnline.set(true);
    } else if (!isOnline && isBrowserOnline) {
      this._isOnline.set(false);
    }

    if (isOnline) {
      onlineHandler();
    }
  }

  private sendNotification(
    id: string,
    title: string,
    message: string,
    onClick?: () => void,
    iconUrl?: string,
  ) {
    const maxLength = 100;
    if (message.length > maxLength) {
      message = `${message.substring(0, maxLength)}...`;
    }

    if (this.notificationOverride) {
      this.notificationOverride(id, title, message, onClick, iconUrl);
      return;
    }

    try {
      const options: ChromeNotificationOptions = {
        body: message,
        tag: message,
        requireInteraction: false,
        silent: true,
        ...(iconUrl ? { icon: iconUrl } : {}),
      };

      const notification = new Notification(title, options);
      notification.onclick = () => {
        window.focus();

        if (onClick) {
          // handle any custom click handlers
          onClick();
        }

        notification.close();
      };
    } catch (e) {
      console.error('Something went wrong pushing the notification', e);
    }
  }

  private getOS(): string {
    const userAgent = this.window.navigator.userAgent;
    if (userAgent.indexOf('Win') !== -1) {
      return 'windows';
    } else if (userAgent.indexOf('Mac') !== -1) {
      return 'mac';
    } else if (userAgent.indexOf('Linux') !== -1) {
      return 'linux';
    }

    return 'unknown';
  }
}
